import React from 'react';
import { useTranslation } from 'react-i18next';

import { ExternalLink } from 'src/api/contentful/contentTypes.cloverdotcom';
import { getReferralType } from 'src/components/SalesChannel/Util';
import { useShopTemplateContext } from 'src/components/ShopTemplateContext/ShopTemplateContext';
import { TrackedLink } from 'src/components/TrackedLink/TrackedLink';
import { FlatEntry } from 'src/services/cms/flattenContentful';
import { isChildrenDefined } from 'src/utils/ChildrenCheck';
import { getUrlWithParamsOrPdpRedirect, shouldRedirectExistingMerchant } from 'src/utils/ExistingMerchant';
import { getEntryDataAttributes } from 'src/utils/GetEntryDataAttributes';
import { testAttributeWithPrefix } from 'src/utils/Serenity';
import { ContentLinkV2Props } from './ContentLinkV2';

export interface ExternalLinkV2Props extends ContentLinkV2Props<FlatEntry<ExternalLink>> {
  titleTag?: string;
  dataTestPrefix?: string;
}

export const ExternalLinkV2 = React.forwardRef<HTMLAnchorElement, ExternalLinkV2Props>(({
  entry,
  target,
  rel,
  children,
  hideTitle,
  titleTag = 'span',
  dataTestPrefix,
  ...props
}, ref) => {
  if (!entry) return null;

  const shopTemplate = useShopTemplateContext();

  const { hint: helpText, newTab } = entry;
  let { title: linkText } = entry;

  const { t } = useTranslation();

  let href = getUrlWithParamsOrPdpRedirect(entry);
  const isPhoneNumber = (href = '') => href.includes('tel:');

  const shouldRedirectToPDP = shouldRedirectExistingMerchant(entry);

  if (getReferralType() === 'BC' && isPhoneNumber(href)) return null;

  if (shopTemplate.reseller?.phoneNumberLink && isPhoneNumber(href)) {
    linkText = shopTemplate.reseller.phoneNumberLink.linkText;
    href = shopTemplate.reseller.phoneNumberLink.url;
  }

  const dataTest = testAttributeWithPrefix('external-link', linkText, dataTestPrefix);

  const childrenIsValid = isChildrenDefined(children);

  return (
    <TrackedLink
      ref={ref}
      href={href || undefined}
      target={target || (newTab && '_blank') || undefined}
      title={helpText || linkText}
      rel={[newTab && 'noopener', rel].filter(s => !!s).join(' ') || undefined}
      data-test={dataTest}
      {...getEntryDataAttributes(entry)}
      {...props}
    >
      {(shouldRedirectToPDP && !childrenIsValid) && React.createElement(titleTag, { 'data-test': 'text' }, t('contact-sales'))}
      {!(shouldRedirectToPDP || hideTitle) && React.createElement(titleTag, '', linkText)}
      {childrenIsValid && children}
    </TrackedLink>
  );
});

ExternalLinkV2.displayName = 'ExternalLinkV2';
